import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Link2,
  Link2Off,
  Upload,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  FolderOpen,
  FileSpreadsheet,
  Clock,
  Zap,
  X,
  ChevronRight,
  Info,
} from 'lucide-react'
import * as api from '../services/api'
import type {
  SmartsheetConnectionStatus,
  SmartsheetWorkspace,
  SmartsheetSheet,
  GeneratedOutput,
} from '../types'

function ConnectionCard({
  status,
  onConnect,
  onDisconnect,
  loading,
}: {
  status: SmartsheetConnectionStatus | null
  onConnect: (token: string) => void
  onDisconnect: () => void
  loading: boolean
}) {
  const [showTokenInput, setShowTokenInput] = useState(false)
  const [token, setToken] = useState('')

  const handleConnect = () => {
    if (token.trim()) {
      onConnect(token.trim())
      setToken('')
      setShowTokenInput(false)
    }
  }

  if (!status?.connected) {
    return (
      <div className="card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-slate-700/50 rounded-xl flex items-center justify-center">
            <Link2Off className="w-6 h-6 text-slate-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-100">Smartsheets Not Connected</h3>
            <p className="text-sm text-slate-400 mt-1">
              Connect your Smartsheets account to create workspaces and import data
            </p>
          </div>
        </div>

        {showTokenInput ? (
          <div className="mt-4 space-y-3">
            <div>
              <label className="label">Smartsheets API Token</label>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter your API token"
                className="input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Get your token from Smartsheets &gt; Account &gt; Apps & Integrations &gt; API
                Access
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleConnect}
                disabled={!token.trim() || loading}
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    Connect
                  </>
                )}
              </button>
              <button onClick={() => setShowTokenInput(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowTokenInput(true)} className="btn-primary mt-4">
            <Link2 className="w-4 h-4" />
            Connect Smartsheets
          </button>
        )}

        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-green-400 mt-0.5" />
            <div className="text-sm">
              <p className="text-green-300 font-medium">Enterprise License</p>
              <p className="text-green-400/80 mt-1">
                Full API access with Enterprise license including Reports and Dashboards.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-100">Smartsheets Connected</h3>
            <p className="text-sm text-slate-400 mt-1">
              Connected as <span className="text-slate-300">{status.smartsheet_email}</span>
            </p>
            {status.smartsheet_name && (
              <p className="text-xs text-slate-500 mt-1">{status.smartsheet_name}</p>
            )}
          </div>
        </div>
        <button
          onClick={onDisconnect}
          disabled={loading}
          className="btn-ghost btn-sm text-red-400 hover:text-red-300"
        >
          <Link2Off className="w-4 h-4" />
          Disconnect
        </button>
      </div>
    </div>
  )
}

function WorkspaceCard({
  workspace,
  onCreate,
  onDisconnect,
  loading,
  disconnecting,
}: {
  workspace: SmartsheetWorkspace | null
  onCreate: () => void
  onDisconnect: () => void
  loading: boolean
  disconnecting: boolean
}) {
  if (!workspace) {
    return (
      <div className="card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-slate-700/50 rounded-xl flex items-center justify-center">
            <FolderOpen className="w-6 h-6 text-slate-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-100">No Workspace Connected</h3>
            <p className="text-sm text-slate-400 mt-1">
              Create a Smartsheets workspace for this project to organize sheets
            </p>
          </div>
        </div>
        <button onClick={onCreate} disabled={loading} className="btn-primary mt-4">
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <FolderOpen className="w-4 h-4" />
              Create Workspace
            </>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <FolderOpen className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-100">{workspace.workspace_name}</h3>
            <p className="text-sm text-slate-400 mt-1">
              Created {new Date(workspace.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {workspace.workspace_url && (
            <a
              href={workspace.workspace_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost btn-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Open
            </a>
          )}
          <button
            onClick={onDisconnect}
            disabled={disconnecting}
            className="btn-ghost btn-sm text-amber-400 hover:text-amber-300"
            title="Disconnect workspace (does not delete from Smartsheets)"
          >
            {disconnecting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Link2Off className="w-4 h-4" />
            )}
            Disconnect
          </button>
        </div>
      </div>
    </div>
  )
}

function SheetsList({
  sheets,
  onSync,
  syncing,
}: {
  sheets: SmartsheetSheet[]
  onSync: () => void
  syncing: boolean
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-100">Sheets ({sheets.length})</h3>
        <button onClick={onSync} disabled={syncing} className="btn-ghost btn-sm">
          {syncing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              Sync
            </>
          )}
        </button>
      </div>
      {sheets.length === 0 ? (
        <p className="text-slate-500 text-sm">No sheets imported yet</p>
      ) : (
        <div className="space-y-2">
          {sheets.map((sheet) => (
            <div key={sheet.id} className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-slate-400" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-200 truncate">{sheet.sheet_name}</p>
                <p className="text-xs text-slate-500">
                  {sheet.row_count ? `${sheet.row_count} rows • ` : ''}Updated{' '}
                  {sheet.last_sync_at ? new Date(sheet.last_sync_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              {sheet.sheet_url && (
                <a
                  href={sheet.sheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost btn-icon btn-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ImportModal({
  outputs,
  onClose,
  onImport,
  importing,
}: {
  outputs: GeneratedOutput[]
  onClose: () => void
  onImport: (outputIds: string[], createWorkspace: boolean) => void
  importing: boolean
}) {
  const [selectedOutputs, setSelectedOutputs] = useState<string[]>([])
  const [createWorkspace, setCreateWorkspace] = useState(true)

  const toggleOutput = (id: string) => {
    setSelectedOutputs((prev) =>
      prev.includes(id) ? prev.filter((oid) => oid !== id) : [...prev, id]
    )
  }

  const selectAll = () => {
    if (selectedOutputs.length === outputs.length) {
      setSelectedOutputs([])
    } else {
      setSelectedOutputs(outputs.map((o) => o.id))
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal max-w-lg animate-scaleIn" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="text-lg font-semibold text-slate-100">Import to Smartsheets</h3>
          <button onClick={onClose} className="btn-ghost btn-icon btn-sm" disabled={importing}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="modal-body space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">Select Outputs to Import</label>
              <button onClick={selectAll} className="text-xs text-blue-400 hover:text-blue-300">
                {selectedOutputs.length === outputs.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {outputs
                .filter((o) => o.status === 'completed')
                .map((output) => (
                  <label
                    key={output.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedOutputs.includes(output.id)
                        ? 'bg-blue-500/20 border border-blue-500/50'
                        : 'bg-slate-800/30 border border-transparent hover:border-slate-600/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedOutputs.includes(output.id)}
                      onChange={() => toggleOutput(output.id)}
                      className="checkbox"
                    />
                    <FileSpreadsheet className="w-5 h-5 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-200 truncate">
                        {output.filename}
                      </p>
                      <p className="text-xs text-slate-500">{output.output_type}</p>
                    </div>
                  </label>
                ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="createWorkspace"
              checked={createWorkspace}
              onChange={(e) => setCreateWorkspace(e.target.checked)}
              className="checkbox"
            />
            <label htmlFor="createWorkspace" className="text-sm text-slate-300">
              Create workspace if it doesn't exist
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={importing}>
            Cancel
          </button>
          <button
            onClick={() => onImport(selectedOutputs, createWorkspace)}
            disabled={importing || selectedOutputs.length === 0}
            className="btn-primary"
          >
            {importing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Import {selectedOutputs.length} file{selectedOutputs.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Smartsheets() {
  const { projectId } = useParams<{ projectId: string }>()
  const [connectionStatus, setConnectionStatus] = useState<SmartsheetConnectionStatus | null>(null)
  const [workspace, setWorkspace] = useState<SmartsheetWorkspace | null>(null)
  const [sheets, setSheets] = useState<SmartsheetSheet[]>([])
  const [outputs, setOutputs] = useState<GeneratedOutput[]>([])
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [creatingWorkspace, setCreatingWorkspace] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [disconnectingWorkspace, setDisconnectingWorkspace] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [statusRes, sheetsRes, outputsRes] = await Promise.all([
        api.getSmartsheetStatus(projectId),
        api.getSmartsheetSheets(projectId).catch(() => ({ data: [] })),
        api.getOutputs(projectId),
      ])
      setConnectionStatus(statusRes.data)
      setSheets(sheetsRes.data)
      setOutputs(outputsRes.data.outputs || [])

      // Try to get workspace if connected
      if (statusRes.data.connected) {
        try {
          const workspaceRes = await api.getSmartsheetWorkspace(projectId)
          setWorkspace(workspaceRes.data)
        } catch {
          setWorkspace(null)
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleConnect = async (token: string) => {
    if (!projectId) return
    setConnecting(true)
    setError(null)
    try {
      await api.connectSmartsheets(projectId, { api_token: token })
      await fetchData()
    } catch (err) {
      console.error('Failed to connect:', err)
      setError('Failed to connect to Smartsheets. Please check your API token.')
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!projectId) return
    if (!confirm('Are you sure you want to disconnect from Smartsheets?')) return

    try {
      await api.disconnectSmartsheets(projectId)
      setConnectionStatus(null)
      setWorkspace(null)
      setSheets([])
    } catch (err) {
      console.error('Failed to disconnect:', err)
      setError('Failed to disconnect from Smartsheets')
    }
  }

  const handleCreateWorkspace = async () => {
    if (!projectId) return
    setCreatingWorkspace(true)
    setError(null)
    try {
      const response = await api.createSmartsheetWorkspace(projectId)
      setWorkspace(response.data)
    } catch (err) {
      console.error('Failed to create workspace:', err)
      setError('Failed to create workspace')
    } finally {
      setCreatingWorkspace(false)
    }
  }

  const handleDisconnectWorkspace = async () => {
    if (!projectId) return
    if (
      !confirm(
        'Disconnect this workspace from SprintForge?\n\n' +
          'This only removes the link - it does NOT delete anything from Smartsheets.\n' +
          'You can re-import to a new workspace after disconnecting.'
      )
    )
      return

    setDisconnectingWorkspace(true)
    setError(null)
    try {
      await api.deleteSmartsheetWorkspace(projectId)
      setWorkspace(null)
      setSheets([])
    } catch (err) {
      console.error('Failed to disconnect workspace:', err)
      setError('Failed to disconnect workspace')
    } finally {
      setDisconnectingWorkspace(false)
    }
  }

  const handleSync = async () => {
    if (!projectId) return
    setSyncing(true)
    setError(null)
    try {
      await api.syncSmartsheets(projectId)
      await fetchData()
    } catch (err) {
      console.error('Failed to sync:', err)
      setError('Failed to sync with Smartsheets')
    } finally {
      setSyncing(false)
    }
  }

  const handleImport = async (outputIds: string[], createWorkspace: boolean) => {
    if (!projectId) return
    setImporting(true)
    setError(null)
    setSuccessMessage(null)
    try {
      const response = await api.importToSmartsheets(projectId, outputIds, createWorkspace)
      const result = response.data
      setShowImportModal(false)

      // Build success message with formula status
      const sheetsWithErrors = result.sheets.filter((s) => s.formula_errors?.length > 0)
      const totalFormulas = result.sheets.reduce((sum, s) => sum + (s.formulas_applied || 0), 0)

      let message = `Imported ${result.sheets_imported} sheets to Smartsheets.`
      if (totalFormulas > 0) {
        message += ` Applied ${totalFormulas} formulas automatically.`
      }
      if (sheetsWithErrors.length > 0) {
        message += ` ${sheetsWithErrors.length} sheet(s) had formula errors - check Post-Import Tasks.`
      }
      message += ` Created ${result.post_import_tasks_created} post-import tasks.`

      setSuccessMessage(message)
      await fetchData()
    } catch (err) {
      console.error('Failed to import:', err)
      setError('Failed to import to Smartsheets')
    } finally {
      setImporting(false)
    }
  }

  const completedOutputs = outputs.filter((o) => o.status === 'completed')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Smartsheets Integration</h1>
          <p className="text-slate-400 mt-1">Connect and sync your project data with Smartsheets</p>
        </div>
        {connectionStatus?.connected && completedOutputs.length > 0 && (
          <button onClick={() => setShowImportModal(true)} className="btn-primary">
            <Upload className="w-4 h-4" />
            Import to Smartsheets
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="alert-error">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-emerald-300">{successMessage}</div>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-emerald-400 hover:text-emerald-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card">
              <div className="flex items-center gap-4">
                <div className="skeleton w-12 h-12 rounded-xl" />
                <div className="flex-1">
                  <div className="skeleton h-5 w-48 mb-2" />
                  <div className="skeleton h-4 w-32" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Connection Status */}
          <ConnectionCard
            status={connectionStatus}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            loading={connecting}
          />

          {/* Workspace (only show if connected) */}
          {connectionStatus?.connected && (
            <>
              <WorkspaceCard
                workspace={workspace}
                onCreate={handleCreateWorkspace}
                onDisconnect={handleDisconnectWorkspace}
                loading={creatingWorkspace}
                disconnecting={disconnectingWorkspace}
              />

              {/* Sheets List */}
              <SheetsList sheets={sheets} onSync={handleSync} syncing={syncing} />

              {/* Post-Import Tasks Link */}
              {sheets.length > 0 && (
                <div className="card bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-100">Post-Import Tasks</h3>
                        <p className="text-sm text-slate-400 mt-1">
                          Complete manual configuration tasks for full functionality
                        </p>
                      </div>
                    </div>
                    <Link to={`/projects/${projectId}/post-import`} className="btn-ghost">
                      View Tasks
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}

          {/* No Outputs Warning */}
          {connectionStatus?.connected && completedOutputs.length === 0 && (
            <div className="card text-center py-8">
              <FileSpreadsheet className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-300 mb-2">No Outputs to Import</h3>
              <p className="text-slate-500 mb-4">
                Generate CSV outputs first before importing to Smartsheets
              </p>
              <Link to={`/projects/${projectId}/outputs`} className="btn-primary">
                <Zap className="w-4 h-4" />
                Generate Outputs
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          outputs={outputs}
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
          importing={importing}
        />
      )}
    </div>
  )
}
